import React, { useEffect, useState } from 'react';
import { Box, Button, Flex, IconButton, Text } from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LuChevronLeft } from 'react-icons/lu';
import { usePhoneShell, usePhoneTopBarConfig } from './PhoneShellContext';

// Pages the More sheet opens. Their bar starts with "‹ More", which reopens
// the sheet, and centres the title. Reached some other way inside the app
// (Home's "See all", a link in Settings; the sheet navigates with
// `state.fromMore`), it starts with a plain back chevron instead.
const MORE_PAGES = ['/accounts', '/source-files', '/categories', '/tags', '/goals', '/members'];

// The title each route shows until its page declares its own bar.
const DEFAULT_TITLES = {
  '/dashboard': 'tabs.home',
  '/movements': 'movements',
  '/chat': 'tabs.ask',
  '/accounts': 'accounts',
  '/source-files': 'statements',
  '/categories': 'categories',
  '/tags': 'tags',
  '/goals': 'goals',
  '/members': 'members',
};

const BAR_HEIGHT = '44px';

// Whether a scroll event came from a list scrolling inside the page (the Ask
// conversation), rather than from a sheet or a sideways chip row.
const isPageScroller = (el) => el instanceof Element
  && el.scrollHeight > el.clientHeight + 1
  && !el.closest('[role="dialog"], .chakra-modal__content-container, [data-no-focus-lock]');

/**
 * The phone's top bar: fixed under the status bar, the page title on the
 * left and at most two 44px actions on the right. Pages declare what it
 * shows with `usePhoneTopBar`; a page reached from More gets "‹ More" and a
 * centred title. A hairline appears once content scrolls under it: the page
 * itself, or a list that scrolls inside it (the Ask conversation). A page can
 * also say so itself with the config's `scrolled`.
 */
export default function PhoneTopBar() {
  const { t } = useTranslation('navigation');
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();
  const { openMore } = usePhoneShell();
  const config = usePhoneTopBarConfig();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    setScrolled(window.scrollY > 0);
    // Capture, because a scroll inside an element does not bubble.
    const onScroll = (event) => {
      const el = event.target;
      if (el === document || el === document.documentElement || el === document.body) {
        setScrolled(window.scrollY > 0);
      } else if (isPageScroller(el)) {
        setScrolled(window.scrollY > 0 || el.scrollTop > 0);
      }
    };
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () => document.removeEventListener('scroll', onScroll, { capture: true });
  }, [pathname]);

  if (config?.hidden) return null;

  const morePage = MORE_PAGES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  const fromMore = morePage && Boolean(location.state?.fromMore);
  // The router numbers its entries from 0, the one the app loaded on. On that
  // one there is nothing inside the app to go back to (and an entry whose
  // state another writer wiped counts as that too).
  const canGoBack = (window.history.state?.idx ?? 0) > 0;
  const titleKey = DEFAULT_TITLES[Object.keys(DEFAULT_TITLES).find((p) => pathname.startsWith(p))];
  const title = config?.title ?? (titleKey ? t(titleKey) : '');
  const backButton = morePage && !fromMore && canGoBack && (
    <IconButton
      variant="ghost"
      color="phone.link"
      icon={<LuChevronLeft size={26} />}
      minW="44px"
      minH="44px"
      aria-label={t('more.back')}
      onClick={() => navigate(-1)}
    />
  );
  const left = config?.left !== undefined ? config.left : backButton || (morePage && (
    <Button
      variant="ghost"
      color="phone.link"
      fontSize="17px"
      fontWeight="400"
      px="4px"
      // The theme's 44px minimum stops at `md`, which a landscape phone is.
      minH="44px"
      leftIcon={<LuChevronLeft size={24} />}
      iconSpacing="0"
      onClick={openMore}
    >
      {t('tabs.more')}
    </Button>
  ));
  const right = config?.right ?? null;
  const centered = config?.centered ?? Boolean(left);
  const hairline = config?.scrolled ?? scrolled;

  return (
    <Box
      as="header"
      position="fixed"
      top={0}
      left={0}
      right={0}
      zIndex="sticky"
      bg="redesign.bg.page"
      pt="env(safe-area-inset-top)"
      pl="env(safe-area-inset-left)"
      pr="env(safe-area-inset-right)"
      boxShadow={hairline ? 'inset 0 -1px 0 var(--chakra-colors-redesign-border)' : 'none'}
      transition="box-shadow 0.15s"
    >
      {centered ? (
        <Box display="grid" gridTemplateColumns="1fr auto 1fr" alignItems="center" h={BAR_HEIGHT} px="8px">
          <Flex justify="flex-start" minW={0}>{left}</Flex>
          <Text as="h1" textStyle="phoneHeadline" color="redesign.heading" noOfLines={1} textAlign="center">
            {title}
          </Text>
          <Flex justify="flex-end" gap="4px" minW={0}>{right}</Flex>
        </Box>
      ) : (
        <Flex align="center" justify="space-between" h={BAR_HEIGHT} pl="16px" pr="8px" gap="8px">
          <Text as="h1" textStyle="phoneTitle" letterSpacing="-0.02em" color="redesign.heading" noOfLines={1}>
            {title}
          </Text>
          <Flex align="center" gap="4px" flexShrink={0}>{right}</Flex>
        </Flex>
      )}
    </Box>
  );
}
